import { type ActivityOf, getItem } from '@wyn/content';
import type { SessionView } from '@wyn/contracts';
import { FISHING_BAND, type FishingSim, fishingSim, type InputTrace } from '@wyn/game-core';
import { HoldButton, Progress, Stage, Track } from './parts.tsx';
import { useRealtime } from './useRealtime.ts';

const FLOAT_DIP = { wait: 0, nib: 7, bite: 34 } as const;

/** The Wharf: strike on the true bite, then reel to keep the line in the band. */
export function FishingGame({ session, activity, onDone }: { session: SessionView; activity: ActivityOf<'fishing'>; onDone: (trace: InputTrace) => void }) {
  const rt = useRealtime<FishingSim>({
    create: () => fishingSim(activity, session.level, session.seed!),
    onDone: (_sim, trace) => onDone(trace),
  });
  const sim = rt.sim;
  if (!sim) return null;
  const t = sim.tick / 60;
  const reeling = sim.phase !== 'bite';
  const dip = sim.phase === 'bite' ? FLOAT_DIP[sim.float] + (sim.float === 'wait' ? Math.sin(t * 4) * 2 : 0) : 34;
  const fish = getItem(activity.catch).name.toLowerCase();

  return (
    <Stage fx={rt.fx}>
      <p className="text-sm text-ink-dim">
        {reeling
          ? <>Hold <b>Reel</b> (or Space) to add tension, let go to give line. Keep the needle in the green band to tire the {fish}. Pinned at the top, the line snaps; slack too long and it slips the hook.</>
          : <>Watch the float. Nibbles are small dips, so ignore them. When it plunges under, <b>Strike</b> (or Space) fast. Strike early twice and the fish are gone.</>}
      </p>
      {!reeling ? (
        <div className="relative h-32 overflow-hidden border border-rule bg-gradient-to-b from-[#20303a] to-[#142028]">
          <div className="absolute inset-x-0 top-[52px] h-px bg-[#3f5866]" />
          <div
            className="absolute left-1/2 top-[38px] h-6 w-3.5 -ml-[7px] rounded-full bg-[linear-gradient(#c9503f_50%,#eee_50%)] transition-transform duration-100"
            style={{ transform: `translateY(${dip}px)` }}
          />
          <div className="absolute inset-x-0 bottom-2 text-center font-mono text-sm text-ink-dim" data-testid="float">
            {sim.float === 'bite' ? '!!' : sim.float === 'nib' ? 'nibble…' : 'Waiting…'}
          </div>
        </div>
      ) : (
        <>
          <div className="relative h-24 overflow-hidden border border-rule bg-gradient-to-b from-[#18262e] to-[#0f1a20]">
            <div className="absolute top-8 text-3xl" style={{ left: `${78 - sim.tired * 68 + Math.sin(t * 7) * 2}%` }}>🐟</div>
          </div>
          <Track value={sim.tension} band={[...FISHING_BAND]} danger={93} label="Line tension" />
          <Progress value={sim.tired} label={`The ${fish} tiring`} />
        </>
      )}
      <HoldButton held={rt.isHeld()} {...rt.holdProps()} disabled={!!sim.result}>
        {reeling ? '🧵 Reel' : '🎣 Strike'}
      </HoldButton>
    </Stage>
  );
}
